/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import axios, { AxiosResponse } from 'axios';
import {
  createContext,
  type Dispatch,
  type PropsWithChildren,
  type SetStateAction,
  useEffect,
  useState
} from 'react';

import * as authHelper from '../_helpers';
import { type AuthModel, type UserModel } from '@/auth';
import { enqueueSnackbar } from 'notistack';

const API_URL = import.meta.env.VITE_APP_API_URL;

export const GET_USER_BY_ACCESSTOKEN_URL = `${API_URL}/user`;
export const LOGIN_URL = `${API_URL}/login`;
export const REGISTER_URL = `${API_URL}/register`;
export const REQUEST_PASSWORD_URL = `${API_URL}/password/request-reset`;

interface AuthContextProps {
  isLoading: boolean;
  auth: AuthModel | undefined;
  saveAuth: (auth: AuthModel | undefined) => void;
  currentUser: UserModel | undefined;
  setCurrentUser: Dispatch<SetStateAction<UserModel | undefined>>;
  login: (email: string, password: string) => Promise<void>;
  register: (
    email: string,
    password: string,
    password_confirmation: string,
    first_name: string,
    last_name: string
  ) => Promise<boolean>;
  requestPassword: (email: string) => Promise<boolean>;
  getUser: () => Promise<AxiosResponse<any>>;
  logout: () => void;
  verify: () => Promise<void>;
  impersonate: (p_id: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextProps | null>(null);

const AuthProvider = ({ children }: PropsWithChildren) => {
  const [loading, setLoading] = useState(true);
  const [auth, setAuth] = useState<AuthModel | undefined>(authHelper.getAuth());
  const [currentUser, setCurrentUser] = useState<UserModel | undefined>();

  const verify = async () => {
    if (!auth) return;

    try {
      const { data: user } = await getUser();
      setCurrentUser(user);
    } catch {
      saveAuth(undefined);
      setCurrentUser(undefined);
    }
  };

  useEffect(() => {
    verify().finally(() => setLoading(false));
  }, []);

  const saveAuth = (auth: AuthModel | undefined) => {
    setAuth(auth);
    auth ? authHelper.setAuth(auth) : authHelper.removeAuth();
  };

  const login = async (email: string, password: string) => {
    try {
      const { data: auth } = await axios.post<AuthModel>(LOGIN_URL, { email, password });
      saveAuth(auth);
      const { data: user } = await getUser();
      setCurrentUser(user);
    } catch (err: any) {
      saveAuth(undefined);
      enqueueSnackbar(
        err.response?.status === 401
          ? 'E-Mail oder Passwort ist falsch.'
          : 'Beim Login ist ein Fehler aufgetreten.',
        { variant: 'error' }
      );
    }
  };

  const register = async (
    email: string,
    password: string,
    password_confirmation: string,
    first_name: string,
    last_name: string
  ): Promise<boolean> => {
    try {
      const { data: auth } = await axios.post<AuthModel>(REGISTER_URL, {
        email,
        password,
        password_confirmation,
        first_name,
        last_name
      });

      if (auth?.access_token) {
        saveAuth(auth);
        const { data: user } = await getUser();
        setCurrentUser(user);
        return true;
      }
      return false;
    } catch (error: any) {
      saveAuth(undefined);
      enqueueSnackbar(error.response?.data?.detail || 'Registrierung fehlgeschlagen.', {
        variant: 'error'
      });
      return false;
    }
  };

  /**
   * Passwort-Reset anfordern
   * → Erfolg wird IMMER angezeigt (Security Best Practice)
   */
  const requestPassword = async (email: string): Promise<boolean> => {
    try {
      await axios.post(REQUEST_PASSWORD_URL, { email });

      return true;
    } catch {
      enqueueSnackbar('Der Vorgang konnte nicht gestartet werden. Bitte später erneut versuchen.', {
        variant: 'error'
      });
      return false;
    }
  };

  const getUser = async () => {
    return axios.get<UserModel>(GET_USER_BY_ACCESSTOKEN_URL);
  };

  const logout = () => {
    saveAuth(undefined);
    setCurrentUser(undefined);
  };

  const impersonate = async (p_id: string) => {
    try {
      const { data: auth } = await axios.post<AuthModel>(`${API_URL}/admin/impersonate`, { p_id });

      saveAuth(auth);
      const { data: user } = await getUser();
      setCurrentUser(user);
      window.location.href = '/';
    } catch {
      enqueueSnackbar('Benutzer konnte nicht übernommen werden.', {
        variant: 'error'
      });
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isLoading: loading,
        auth,
        saveAuth,
        currentUser,
        setCurrentUser,
        login,
        register,
        requestPassword,
        getUser,
        logout,
        verify,
        impersonate
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext, AuthProvider };
